import Setting from './utils/setting.mjs';
import { registerHiddenSetting } from './share.mjs';
import {
	CUSTOM_LOGO_STORAGE_KEY, customLogoEnabled, customLogoFileError, isCustomLogoDataUrl,
} from './customlogo-utils.mjs';

// Initialize settings immediately so other modules can access them
const settings = { speed: { value: 1.0 } };

// Track settings that need DOM changes after early initialization
const deferredDomSettings = new Set();
const defaultLogoSrc = 'images/logos/logo-corner.svg';
// Matches the alt text baked into the header partials, so a fallback cannot disagree
// with the markup it replaces.
const defaultLogoAlt = 'WeatherStar 4000+';

let unitChangefirstRunDone = false;

// don't show checkboxes for these settings
const hiddenSettingNames = [
	'scanLines',

	// wide, portrait and enhanced are handled by a dropdown which sets these individual settings accordingly
	'wide',
	'portrait',
	'enhanced',
];

// Declare change functions first, before they're referenced in init() to avoid the Temporal Dead Zone (TDZ)
const wideScreenChange = (value) => {
	const container = document.querySelector('#divTwc');
	if (!container) {
		// DOM not ready; defer enabling if set
		if (value) {
			deferredDomSettings.add('wide');
		}
		return;
	}

	if (value) {
		container.classList.add('wide');
	} else {
		container.classList.remove('wide');
	}
	// Trigger resize to recalculate scaling for new width
	window.dispatchEvent(new Event('resize'));
};

const portraitChange = (value) => {
	const container = document.querySelector('#divTwc');
	if (!container) {
		// DOM not ready; defer enabling if set
		if (value) {
			deferredDomSettings.add('portrait');
		}
		return;
	}

	if (value) {
		container.classList.add('portrait');
	} else {
		container.classList.remove('portrait');
	}
	// Trigger resize to recalculate scaling for new width
	window.dispatchEvent(new Event('resize'));
};

const enhancedChange = (value) => {
	const container = document.querySelector('#divTwc');
	if (!container) {
		// DOM not ready; defer enabling if set
		if (value) {
			deferredDomSettings.add('enhanced');
		}
		return;
	}

	if (value) {
		container.classList.add('enhanced');
	} else {
		container.classList.remove('enhanced');
	}
};

const viewModeChange = (value) => {
	// set the appropriate mode bits which triggers change actions above
	switch (value) {
		case 'wide':
			settings.wide.value = true;
			settings.enhanced.value = false;
			settings.portrait.value = false;
			break;
		case 'wide-enhanced':
			settings.wide.value = true;
			settings.enhanced.value = true;
			settings.portrait.value = false;
			break;
		case 'portrait-enhanced':
			settings.wide.value = false;
			settings.enhanced.value = true;
			settings.portrait.value = true;
			break;
		default:
			settings.wide.value = false;
			settings.enhanced.value = false;
			settings.portrait.value = false;
	}
	// notify displays to perform data updates or redraws as required
	window.dispatchEvent(new Event('display-mode-change'));
};

const kioskChange = (value) => {
	const body = document.querySelector('body');
	if (!body) {
		// DOM not ready; defer enabling if set
		if (value) {
			deferredDomSettings.add('kiosk');
		}
		return;
	}

	if (value) {
		body.classList.add('kiosk');
		document.querySelector('#divTwc')?.classList.add('no-cursor');
	} else {
		body.classList.remove('kiosk');
		document.querySelector('#divTwc')?.classList.remove('no-cursor');
	}
	window.dispatchEvent(new Event('resize'));

	// Conditionally store the kiosk setting based on the "Sticky Kiosk" setting
	// (Need to check if the method exists to handle initialization race condition)
	if (settings.kiosk?.conditionalStoreToLocalStorage) {
		settings.kiosk.conditionalStoreToLocalStorage(value, settings.stickyKiosk?.value);
	}
};

const scanLineChange = (value) => {
	const container = document.getElementById('container');
	const navIcons = document.getElementById('ToggleScanlines');

	if (!container || !navIcons) {
		// DOM not ready; defer enabling if set
		if (value) {
			deferredDomSettings.add('scanLines');
		}
		return;
	}

	const modeSelect = document.getElementById('settings-scanLineMode-label');

	if (value) {
		container.classList.add('scanlines');
		navIcons.classList.add('on');
		navIcons.setAttribute('aria-pressed', 'true');
		modeSelect?.style?.removeProperty('display');
		// size the lines for the current scale now, resize() only does it when the window size changes
		window.dispatchEvent(new Event('scanlines-change'));
	} else {
		// Remove all scanline classes
		container.classList.remove('scanlines');
		navIcons.classList.remove('on');
		navIcons.setAttribute('aria-pressed', 'false');
		if (modeSelect) {
			modeSelect.style.display = 'none';
		}
	}
};

const scanLineModeChange = (_value) => {
	// Only apply if scanlines are currently enabled
	// scaling.mjs re-applies the scan lines at the current scale
	// an event, because scaling.mjs imports settings and importing it here would be circular
	if (settings.scanLines?.value) {
		window.dispatchEvent(new Event('scanlines-change'));
	}
};

const getStoredCustomLogo = () => {
	try {
		const storedLogo = localStorage?.getItem(CUSTOM_LOGO_STORAGE_KEY);
		return isCustomLogoDataUrl(storedLogo) ? storedLogo : null;
	} catch (error) {
		console.warn(`Failed to read custom logo from localStorage: ${error}`);
		return null;
	}
};

const setCustomLogoStatus = (message, isError = false) => {
	const status = document.getElementById('settings-customLogoImage-status');
	if (!status) return;
	status.textContent = message;
	status.classList.toggle('failed', isError);
	status.classList.toggle('loading', !isError && message !== '');
};

const toggleCustomLogoControls = (enabled) => {
	const uploadControls = document.getElementById('settings-customLogoImage-upload');
	if (uploadControls) {
		uploadControls.style.display = enabled ? '' : 'none'; // '' falls back to the stylesheet's layout
	}
};

const applyCustomLogo = () => {
	const storedLogo = getStoredCustomLogo();
	const hasStoredLogo = Boolean(storedLogo);
	const enabled = customLogoEnabled(settings.customLogoImage?.value, storedLogo);
	const container = document.getElementById('container');
	// The only moment #container is absent is the Setting constructor's startup call,
	// and at that point settings.customLogoImage is still being assigned, so there is
	// nothing to apply. The unconditional applyCustomLogo() at the end of
	// DOMContentLoaded does the real work; no deferral is needed here.
	if (!container) return;

	container.classList.toggle('custom-logo-image-enabled', enabled);
	document.querySelectorAll('.logo-corner').forEach((logo) => {
		// A stored value can satisfy isCustomLogoDataUrl and still fail to decode if it
		// was truncated or hand-edited. Without this, every header shows a broken image
		// and the only escape is the Clear button. Assigned rather than added so repeated
		// applyCustomLogo() calls cannot stack handlers.
		logo.onerror = () => {
			if (logo.getAttribute('src') === defaultLogoSrc) return;
			logo.src = defaultLogoSrc;
			logo.alt = defaultLogoAlt;
			setCustomLogoStatus('Stored PNG could not be displayed; using the default logo.', true);
		};
		logo.src = enabled ? storedLogo : defaultLogoSrc;
		logo.alt = enabled ? 'Custom logo' : defaultLogoAlt;
	});

	// Keep the controls — and so the Clear button — reachable whenever a PNG is still
	// stored. Hiding them on the setting alone orphans the stored data with no UI to
	// delete it.
	toggleCustomLogoControls(Boolean(settings.customLogoImage?.value) || hasStoredLogo);
	if (settings.customLogoImage?.value && !hasStoredLogo) {
		setCustomLogoStatus('Upload a PNG to replace the default logo.');
	} else if (enabled) {
		setCustomLogoStatus('Custom PNG logo loaded.');
	} else {
		setCustomLogoStatus('');
	}
};

const saveCustomLogo = (dataUrl) => {
	try {
		localStorage?.setItem(CUSTOM_LOGO_STORAGE_KEY, dataUrl);
		return true;
	} catch (error) {
		console.warn(`Failed to store custom logo: ${error}`);
		setCustomLogoStatus('PNG is too large to store locally.', true);
		return false;
	}
};

const clearCustomLogo = () => {
	localStorage?.removeItem(CUSTOM_LOGO_STORAGE_KEY);
	const input = document.getElementById('settings-customLogoImage-file');
	if (input) {
		input.value = '';
	}
	settings.customLogoImage.value = false;
};

const customLogoImageChange = () => {
	applyCustomLogo();
};

const createCustomLogoUploadControl = () => {
	const wrapper = document.createElement('div');
	wrapper.id = 'settings-customLogoImage-upload';

	// A real <label for> rather than a bare span: every other setting in this panel is a
	// label wrapping its input, so without this the file input has no accessible name.
	const title = document.createElement('label');
	title.textContent = 'Logo PNG ';
	title.htmlFor = 'settings-customLogoImage-file';

	const fileInput = document.createElement('input');
	fileInput.type = 'file';
	fileInput.accept = 'image/png';
	fileInput.id = 'settings-customLogoImage-file';
	fileInput.name = 'settings-customLogoImage-file';

	const clearButton = document.createElement('input');
	clearButton.type = 'button';
	clearButton.value = 'Clear';
	clearButton.id = 'settings-customLogoImage-clear';
	clearButton.name = 'settings-customLogoImage-clear';
	clearButton.addEventListener('click', clearCustomLogo);

	const status = document.createElement('span');
	status.id = 'settings-customLogoImage-status';
	// Upload errors are written here; without a live region they are never announced.
	status.setAttribute('aria-live', 'polite');

	fileInput.addEventListener('change', () => {
		const [file] = fileInput.files ?? [];
		if (!file) {
			return;
		}
		const fileError = customLogoFileError(file);
		if (fileError) {
			fileInput.value = '';
			setCustomLogoStatus(fileError, true);
			return;
		}

		const reader = new FileReader();
		reader.addEventListener('load', () => {
			const dataUrl = typeof reader.result === 'string' ? reader.result : null;
			if (!dataUrl) {
				setCustomLogoStatus('Failed to read PNG file.', true);
				return;
			}
			if (saveCustomLogo(dataUrl)) {
				settings.customLogoImage.value = true;
			}
		});
		reader.addEventListener('error', () => {
			setCustomLogoStatus('Failed to read PNG file.', true);
		});
		reader.readAsDataURL(file);
	});

	wrapper.append(title, fileInput, clearButton, status);
	return wrapper;
};

// Simple global helper to change scanline mode when remote debugging or in kiosk mode
window.changeScanlineMode = (mode) => {
	if (typeof settings === 'undefined' || !settings.scanLineMode) {
		console.error('Settings system not available');
		return false;
	}

	const validModes = ['auto', 'thin', 'medium', 'thick'];
	if (!validModes.includes(mode)) {
		return false;
	}

	settings.scanLineMode.value = mode;
	return true;
};

const unitChange = () => {
	// reload the data at the top level to refresh units
	// after the initial load
	if (unitChangefirstRunDone) {
		window.location.reload();
	}
	unitChangefirstRunDone = true;
};

const init = () => {
	// create settings see setting.mjs for defaults
	settings.wide = new Setting('wide', {
		name: 'Widescreen',
		defaultValue: false,
		changeAction: wideScreenChange,
		sticky: true,
	});
	settings.portrait = new Setting('portrait', {
		name: 'Allow Portrait',
		changeAction: portraitChange,
		defaultValue: false,
		sticky: true,
	});
	settings.enhanced = new Setting('enhanced', {
		name: 'Enhanced Screens',
		defaultValue: false,
		changeAction: enhancedChange,
		sticky: true,
	});
	// widescreen, portrait and enhanced are handled by a dropdown
	// the dropdown change action sets the above bits accordingly
	// first, figure out the default value based on other settings
	// this also enforces rules on how these can be combined
	let viewModeDefault = 'standard';
	if (settings.wide.value && !settings.enhanced.value) viewModeDefault = 'wide';
	if (settings.wide.value && settings.enhanced.value) viewModeDefault = 'wide-enhanced';
	if (settings.portrait.value) viewModeDefault = 'portrait-enhanced';
	settings.viewMode = new Setting('viewMode', {
		name: 'Display mode',
		type: 'select',
		defaultValue: viewModeDefault,
		changeAction: viewModeChange,
		sticky: false,	// not sticky because the above 3 settings are sticky and define this item's starting state
		values: [
			['standard', 'Standard'],
			['wide', 'Widescreen'],
			['wide-enhanced', 'Widescreen enhanced'],
			['portrait-enhanced', 'Portrait enhanced'],
		],
	});
	settings.kiosk = new Setting('kiosk', {
		name: 'Kiosk',
		defaultValue: false,
		changeAction: kioskChange,
		sticky: false,
		stickyRead: true,
	});
	settings.stickyKiosk = new Setting('stickyKiosk', {
		name: 'Sticky Kiosk',
		defaultValue: false,
		sticky: true,
	});
	settings.speed = new Setting('speed', {
		name: 'Speed',
		type: 'select',
		defaultValue: 1.0,
		values: [
			[0.5, 'Very Fast 2x'],
			[0.75, 'Fast 1.5x'],
			[1.0, 'Normal'],
			[1.25, 'Slow 0.75x'],
			[1.5, 'Very Slow 0.67x'],
		],
	});
	settings.scanLines = new Setting('scanLines', {
		name: 'Scan Lines',
		defaultValue: false,
		changeAction: scanLineChange,
		sticky: true,
	});
	settings.scanLineMode = new Setting('scanLineMode', {
		name: 'Scan Line Style',
		type: 'select',
		defaultValue: 'auto',
		changeAction: scanLineModeChange,
		sticky: true,
		values: [
			['auto', 'Auto (Adaptive)'],
			['thin', 'Thin (1x)'],
			['medium', 'Medium (2x)'],
			['thick', 'Thick (3x)'],
		],
		visible: true,
	});
	settings.units = new Setting('units', {
		name: 'Units',
		type: 'select',
		defaultValue: 'us',
		changeAction: unitChange,
		values: [
			['us', 'US'],
			['si', 'Metric'],
		],
	});
	settings.customLogoImage = new Setting('customLogoImage', {
		name: 'Custom Logo PNG',
		defaultValue: false,
		changeAction: customLogoImageChange,
		sticky: true,
	});
	settings.refreshTime = new Setting('refreshTime', {
		type: 'select',
		defaultValue: 600_000,
		sticky: false,
		values: [
			[30_000, 'TESTING'],
			[300_000, '5 minutes'],
			[600_000, '10 minutes'],
			[900_000, '15 minutes'],
			[1_800_000, '30 minutes'],
		],
		visible: false,
	});
};

init();

// generate html objects
document.addEventListener('DOMContentLoaded', () => {
	// Apply any settings that were deferred due to the DOM not being ready when setting were read
	if (deferredDomSettings.size > 0) {
		console.log('Applying deferred DOM settings:', Array.from(deferredDomSettings));

		// Re-apply each pending setting by calling its changeAction with current value
		deferredDomSettings.forEach((settingName) => {
			const setting = settings[settingName];
			if (setting && setting.changeAction && typeof setting.changeAction === 'function') {
				setting.changeAction(setting.value);
			}
		});

		deferredDomSettings.clear();
	}

	// Then generate the settings UI
	const settingElems = Object.values(settings).map((setting) => {
		if (hiddenSettingNames.includes(setting.shortName)) {
			// setting is hidden, register it
			registerHiddenSetting(setting.shortName, setting);
			return false;
		}
		// generate HTML for setting
		return setting.generate();
	}).filter((d) => d);
	const settingsSection = document.querySelector('#settings');
	// the logo upload sits directly after the generated settings, ahead of anything else in the panel
	settingsSection.prepend(...settingElems, createCustomLogoUploadControl());

	// update visibility on some settings
	const modeSelect = document.getElementById('settings-scanLineMode-label');
	const { value } = settings.scanLines;
	if (value) {
		modeSelect?.style?.removeProperty('display');
	} else if (modeSelect) {
		modeSelect.style.display = 'none';
	}
	applyCustomLogo();
});

export default settings;
